import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Career & Identity — the opportunity pipeline (job-search / role board).
 *
 * An Opportunity moves through a simple, honest pipeline: saved → applied →
 * interviewing → offer → closed. It links to the proof (projects, skills,
 * portfolio) that supports it rather than duplicating any of it. No vanity
 * metrics, no scoreboard — just a clear view of what's in play and the next move.
 */

export type OpportunityStage = 'saved' | 'applied' | 'interviewing' | 'offer' | 'closed';
export const OPPORTUNITY_STAGES: readonly OpportunityStage[] = [
  'saved',
  'applied',
  'interviewing',
  'offer',
  'closed',
] as const;

export interface Opportunity extends RecordEnvelope {
  readonly kind: 'opportunity';
  /** The role or opportunity title. */
  readonly title: string;
  readonly organization: string;
  readonly stage: OpportunityStage;
  readonly url: string;
  readonly note: string;
  /** Linked projects/skills/portfolio that prove fit (ids only). */
  readonly linkedIds: readonly EntityId[];
}

export interface OpportunityInput {
  readonly title: string;
  readonly organization?: string;
  readonly url?: string;
  readonly note?: string;
  readonly privacyClass?: PrivacyClass;
}

export interface OpportunityPatch {
  readonly title?: string;
  readonly organization?: string;
  readonly url?: string;
  readonly note?: string;
}

/** An opportunity is "in play" until it is closed. */
export function isOpportunityActive(stage: OpportunityStage): boolean {
  return stage !== 'closed';
}

export interface PipelineSummary {
  readonly total: number;
  readonly active: number;
  readonly byStage: Record<OpportunityStage, number>;
}

/** Pure pipeline roll-up for the Career header. */
export function summarizePipeline(
  opportunities: readonly Pick<Opportunity, 'stage'>[],
): PipelineSummary {
  const byStage: Record<OpportunityStage, number> = {
    saved: 0,
    applied: 0,
    interviewing: 0,
    offer: 0,
    closed: 0,
  };
  let active = 0;
  for (const o of opportunities) {
    byStage[o.stage] += 1;
    if (isOpportunityActive(o.stage)) active += 1;
  }
  return { total: opportunities.length, active, byStage };
}
